<template>
  <table v-if="items.length > 0">
    <button v-if="showBackToTop" @click="scrollToTop" class="back-to-top">
      <i class="bi bi-caret-up-fill"></i>
    </button>

    <thead class="sticky-top thead-to-top">
      <tr>
        <th v-for="header in headers" :key="header.key">
          {{ header.label }}
        </th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="(item, index) in items" :key="item.id">
        <td v-for="header in headers" :key="header.key">
          <template v-if="header.type === 'operationType'">
            <span
              v-if="item.targetTaxOrderId && typeof item.targetTaxOrderId === 'string'"
              class="badge bg-danger me-1"
            >
              T.A
            </span>
            <span
              :class="[
                'badge',
                {
                  'bg-sell': item[header.key] === operationTypes.SELLING,
                  'bg-buy': item[header.key] === operationTypes.BUYING
                }
              ]"
            >
              {{ operationType[item[header.key]] }}
            </span>
          </template>
          <template v-else-if="header.type === 'latency'">
            <div
              style="height: 15px; width: 15px; border-radius: 100%"
              :style="{ backgroundColor: item[header.key] }"
            ></div>
          </template>

          <template v-else-if="header.type === 'actions' && !item.isTpay">
            <div v-if="!collapseActionButtons(header.options)">
              <button
                v-for="option in header.options"
                v-show="option.isVisible !== false"
                @click="$emit(option.emitEvent, item)"
                :key="option.action"
                :style="{ backgroundColor: option.color, color: 'white' }"
                class="action-button"
                :class="option.class"
              >
                {{ option.label }}
              </button>
            </div>
            <div v-else style="width: 165px" class="options-reference">
              <button
                @click="toggleOptionsForItem(index)"
                class="action-button edit collapsed-button px-3 py-0"
                style="margin-left: 110px"
                :class="{ 'is-active': actionOptionId === index }"
                :key="header.key"
              >
                ···
              </button>
              <transition name="fade-slide">
                <div
                  ref="boxRef"
                  v-if="actionOptionId == index"
                  class="d-flex flex-column dropdown-options"
                >
                  <div class="list-group">
                    <a
                      v-for="option in header.options"
                      :key="option.emitEvent"
                      @click="($emit(option.emitEvent, item), (actionOptionId = null))"
                      class="list-group-item list-group-item-action"
                    >
                      {{ option.label }}
                    </a>
                  </div>
                </div>
              </transition>
            </div>
          </template>

          <template v-else-if="header.type === 'info'">
            <button @click="emitInfo(item)" class="action-button info">
              {{ item[header.key] }}
            </button>
          </template>

          <template v-else-if="header.type === 'statusType'">
            {{ statusType[item[header.key]] }}
          </template>

          <template v-else-if="header.type === 'pendencyType'">
            {{ pendencyType[item[header.key]] }}
          </template>

          <template v-else-if="header.type === 'onboardingStatusType'">
            <span
              class="badge"
              :style="{ 'background-color': onboardingStatusType[item[header.key] || '']?.color }"
            >
              {{
                item[header.key] ? onboardingStatusType[item[header.key]].name : emptyPlaceholder
              }}
            </span>
          </template>

          <template v-else-if="header.type === 'userStatusType'">
            <span
              class="badge"
              :style="{ 'background-color': userStatusType[item[header.key] || '']?.color }"
            >
              {{ item[header.key] ? userStatusType[item[header.key]].name : emptyPlaceholder }}
            </span>
          </template>

          <template
            v-else-if="
              header.type === 'currency' || header.type === 'spread' || header.type === 'quotation'
            "
          >
            {{ fetchCurrencySymbol(header, item) || '' }}
            {{ formatNumber(item[header.key], header) }}
          </template>

          <template v-else-if="header.type === 'date'">
            {{
              formatDate(item[header.key], header.format, header.timeZoneOffset) || emptyPlaceholder
            }}
          </template>

          <template v-else-if="header.type === 'check'">
            <input
              class="form-check-input ng-untouched ng-pristine ng-valid"
              type="checkbox"
              :checked="item[header.key]"
              disabled
            />
          </template>

          <template v-else>
            <span
              v-if="item.currencyType === 'USDT' || item.currencyType === 'USDC'"
              class="badge me-1 btc"
              >B
            </span>

            <span
              v-if="header.key === 'clientName' && item.institution"
              class="badge"
              :style="{ 'background-color': institutionType[item.institution].color }"
            >
              {{ institutionType[item.institution].tag }}
            </span>
            {{
              item[header.key] != null
                ? truncateText(item[header.key], 35).toString().toUpperCase()
                : emptyPlaceholder
            }}
            <span
              v-if="header.key === 'clientName' && item.isActive === false && !item.isTpay"
              class="badge bg-danger"
            >
              <i class="bi bi-ban-fill"></i>
              DESATIVADO
            </span>
          </template>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script>
import { TZDate, tz } from '@date-fns/tz'
import { operationTypes, emptyPlaceholder } from '@shared/composables/useGlobalConfig'
import { useInstitution } from '@shared/composables/useInstitution'
import { useOnboardingStatus } from '@shared/composables/useOnboardingStatus'
import { useOperationPendency } from '@shared/composables/useOperationPendency'
import { useOperationStatus } from '@shared/composables/useOperationStatus'
import { useOperationType } from '@shared/composables/useOperationType'
import { userStatus } from '@shared/composables/userStatus'
import { useWindowSize } from '@vueuse/core'
import { parseISO, format } from 'date-fns'
import { ref, onMounted, onBeforeUnmount } from 'vue'

export default {
  name: 'dynamicTable',
  props: {
    headers: {
      type: Array,
      default: () => [],
      required: true
    },
    items: {
      type: Array,
      default: () => [],
      required: true
    }
  },

  setup(props, { emit }) {
    const { statusType } = useOperationStatus()
    const { institutionType } = useInstitution()
    const { operationType } = useOperationType()
    const { pendencyType } = useOperationPendency()
    const { onboardingStatusType } = useOnboardingStatus()
    const { userStatusType } = userStatus()
    const actionOptionId = ref(null)

    const { width } = useWindowSize()

    const emitInfo = (item) => {
      emit('info', item)
    }

    const formatDate = (dateString, dateFormat, timeZoneOffset = 0) => {
      if (!dateString) return ''
      const dateWithTime = parseISO(dateString)
      if (dateWithTime.getFullYear() >= 2999) {
        return 'Sem expiração'
      }
      const timeZone = `Etc/GMT${timeZoneOffset > 0 ? '-' : '+'}${Math.abs(timeZoneOffset)}`
      const zoned = new TZDate(dateWithTime, timeZone)
      return format(zoned, dateFormat, { in: tz(timeZone) })
    }

    const fetchCurrencySymbol = (header, item) => {
      if (header.type === 'currency') {
        return item.currencyType
      } else if (header.type === 'spread') {
        return item.spreadType == 'PERCENTAGE' ? '%' : 'R$'
      }
    }

    const formatNumber = (value, header) => {
      if (value == null) return emptyPlaceholder
      return value.toLocaleString(header.locale, {
        minimumFractionDigits: header.maximumFractionDigits,
        maximumFractionDigits: header.maximumFractionDigits
      })
    }

    const truncateText = (text, maxLength) => {
      if (text.length > maxLength) {
        return text.substring(0, maxLength) + '...'
      }
      return text
    }

    const showBackToTop = ref(false)

    const handleScroll = () => {
      showBackToTop.value = window.scrollY > 1000
    }

    const scrollToTop = () => {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    const collapseActionButtons = (actions) => {
      return actions.length > 3 && width.value < 1550
    }

    const toggleOptionsForItem = (itemId) => {
      actionOptionId.value = actionOptionId.value === itemId ? null : itemId
    }

    onMounted(() => {
      window.addEventListener('scroll', handleScroll)
    })

    onBeforeUnmount(() => {
      window.removeEventListener('scroll', handleScroll)
    })

    return {
      formatDate,
      fetchCurrencySymbol,
      formatNumber,
      statusType,
      pendencyType,
      institutionType,
      operationType,
      onboardingStatusType,
      userStatusType,
      emitInfo,
      operationTypes,
      showBackToTop,
      scrollToTop,
      emptyPlaceholder,
      truncateText,
      collapseActionButtons,
      actionOptionId,
      toggleOptionsForItem
    }
  }
}
</script>

<style>
.btc {
  background-color: #f7931a;
}
.sticky-top {
  z-index: 10;
}

.thead-to-top {
  top: 15px;
}

.back-to-top {
  position: fixed;
  bottom: 12px;
  right: 12px;
  z-index: 1000;
  padding: 11px 15px;
  font-size: 16px;
  border: none;
  border-radius: 9px;
  cursor: pointer;
}

.form-check-input {
  &:checked {
    background-color: var(--secondary-color);
    border-color: var(--secondary-color);
  }

  &[type='checkbox'] {
    border-radius: 0.25em;
    height: 16px;
    width: 16px;
  }
  &:disabled {
    pointer-events: none;
    filter: none;
    opacity: 1;
  }
}
.collapsed-button {
  font-size: 17px !important;
  font-weight: bold !important;
}
.is-active {
  background: #3d69bb !important;
  color: #fff;
}
.options-reference {
  position: relative;
}
.dropdown-options {
  position: absolute;
  background: #ececec;
  border-radius: 8px;
  z-index: 1;
  box-shadow: rgba(149, 157, 165, 0.2) 0px 8px 24px;
}
.dropdown-options a {
  cursor: pointer;
  text-align: start;
}
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.2s ease;
}
.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
